import React from 'react';

let globalErrorCount = 0;
const MAX_ERROR_REPORTS = 5;

const createErrorId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.toString() || 'Unknown client error',
      errorId: createErrorId(),
    };
  }

  componentDidCatch(_error, errorInfo) {
    this.setState({ errorInfo });

    if (globalErrorCount >= MAX_ERROR_REPORTS) return;
    globalErrorCount += 1;

    const errorReport = {
      errorId: this.state.errorId,
      category: 'react_render_error',
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    fetch('/api/log-client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorReport),
    }).catch(() => {});

    if (typeof window.analytics !== 'undefined') {
      window.analytics.track('Error Boundary Triggered', {
        errorId: this.state.errorId,
        path: window.location.pathname,
      });
    }
  }

  handleCopyError = () => {
    const { errorMessage, errorInfo, errorId } = this.state;
    const errorText = [
      `Error ID: ${errorId}`,
      `Timestamp: ${new Date().toISOString()}`,
      `Path: ${window.location.pathname}`,
      `Error: ${errorMessage}`,
      `Component Stack:\n${errorInfo?.componentStack || 'no stack'}`,
    ].join('\n\n');

    navigator.clipboard.writeText(errorText).then(() => {
      alert('Error details copied to clipboard');
    }).catch(() => {
      alert('Unable to copy the error details.');
    });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { errorMessage, errorInfo, errorId } = this.state;
    const isDev = import.meta.env.DEV;
    const supportEmail = 'support@pinngro.com';
    const supportBody = encodeURIComponent(`Please describe what happened.\n\nError ID: ${errorId}\nPage: ${window.location.pathname}`);

    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4 text-slate-950 dark:bg-slate-950 dark:text-white">
        <section className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-7 text-center shadow-xl dark:border-slate-800 dark:bg-slate-900 sm:p-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-2xl font-black text-red-700 dark:bg-red-950 dark:text-red-300" aria-hidden="true">!</div>
          <p className="mt-5 text-sm font-bold uppercase tracking-[0.16em] text-red-600 dark:text-red-400">Unexpected error</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight">Something went wrong</h1>
          <p className="mt-3 text-slate-600 dark:text-slate-300">
            No account or broker credentials were included in this report. Try refreshing, or send support the reference below.
          </p>
          <p className="mt-4 rounded-xl bg-slate-100 px-4 py-3 font-mono text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Error ID: {errorId}
          </p>

          {isDev && (
            <pre className="mt-4 max-h-64 overflow-auto rounded-xl bg-slate-950 p-4 text-left text-xs text-red-200">
              {errorMessage}
              {'\n\n'}
              {errorInfo?.componentStack}
            </pre>
          )}

          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <button type="button" onClick={() => window.location.reload()} className="rounded-xl bg-blue-600 px-5 py-3 font-bold text-white hover:bg-blue-700">
              Refresh page
            </button>
            <a href={`mailto:${supportEmail}?subject=Pinngro%20error%20${errorId}&body=${supportBody}`} className="rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
              Contact support
            </a>
            {isDev && (
              <button type="button" onClick={this.handleCopyError} className="rounded-xl border border-slate-300 px-5 py-3 font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">
                Copy details
              </button>
            )}
          </div>
        </section>
      </main>
    );
  }
}

export default ErrorBoundary;
